import { COLLISION_CELLS_PER_TILE, type MapDocument, type ZoneLayer } from "./types.ts";

/**
 * Zone layers (ADR-054's amendment of 2026-09-27): the canonical form of a layer's type table, the type a
 * cell holds, the map's zones folded across its zone layers, and the cell edits `paintZone` and a zone fill
 * apply. A cell holds a byte, so a layer's table holds at most `MAX_ZONE_TYPES` ids.
 */

/**
 * The most type ids one zone layer's table can index from a byte, and the most zone types a game registers.
 */
export const MAX_ZONE_TYPES = 255;

/**
 * A map's zones folded across its zone layers: one byte per collision cell, 0 unpainted or a 1-based index
 * into `types`, which lists the ids the grid uses in the order a row-major scan first meets them.
 */
export interface FoldedZones {
  readonly types: readonly string[];
  readonly cells: Uint8Array;
}

/**
 * One cell of a `paintZone` stroke: the type id it held and the one it gets, null for unpainted.
 */
export interface ZoneCellChange {
  readonly index: number;
  readonly before: string | null;
  readonly after: string | null;
}

/**
 * Renumbers a grid of 1-based indices into a table so the table lists only the ids the grid uses, in the order
 * a row-major scan first meets them. The grid is renumbered in place.
 *
 * @param types The table the grid indexes.
 * @param cells The grid.
 * @returns The table the grid now indexes.
 * @throws RangeError when a cell indexes beyond the table.
 */
function compactInPlace(types: readonly string[], cells: Uint8Array): string[] {
  const remap = new Uint8Array(types.length + 1);
  const compact: string[] = [];
  for (let i = 0; i < cells.length; i += 1) {
    const value = cells[i] as number;
    if (value === 0) {
      continue;
    }
    if (value > types.length) {
      throw new RangeError(`cell ${i} holds ${value}, beyond the ${types.length} types`);
    }
    if (remap[value] === 0) {
      compact.push(types[value - 1] as string);
      remap[value] = compact.length;
    }
    cells[i] = remap[value] as number;
  }
  return compact;
}

/**
 * Whether a layer is canonical: every cell indexes its table, and the table lists exactly the ids the cells use
 * in first-met order.
 *
 * @param layer The layer.
 * @returns True when canonical.
 */
function isCanonical(layer: ZoneLayer): boolean {
  let met = 0;
  for (let i = 0; i < layer.cells.length; i += 1) {
    const value = layer.cells[i] as number;
    if (value > met + 1) {
      return false;
    }
    if (value === met + 1) {
      met += 1;
    }
  }
  return met === layer.types.length;
}

/**
 * The canonical form of a zone layer: its table the ids its cells use, in the order a row-major scan first meets
 * them, and its cells renumbered to match, so the layer depends only on what is painted.
 *
 * @param layer The layer.
 * @returns The same layer when already canonical, else a canonical copy.
 * @throws RangeError when a cell indexes beyond the table.
 */
export function canonicalZoneLayer(layer: ZoneLayer): ZoneLayer {
  if (isCanonical(layer)) {
    return layer;
  }
  const cells = new Uint8Array(layer.cells);
  const types = compactInPlace(layer.types, cells);
  return { ...layer, types, cells };
}

/**
 * The type id a cell of a zone layer holds.
 *
 * @param layer The layer.
 * @param index The cell's index in the collision grid.
 * @returns The id, or null for an unpainted cell.
 * @throws RangeError when the index lies outside the grid.
 */
export function zoneTypeAt(layer: ZoneLayer, index: number): string | null {
  if (!Number.isInteger(index) || index < 0 || index >= layer.cells.length) {
    throw new RangeError(`cell index ${index} is outside the grid of ${layer.cells.length}`);
  }
  const value = layer.cells[index] as number;
  return value === 0 ? null : (layer.types[value - 1] ?? null);
}

/**
 * Folds a map's zone layers into one grid: where two paint a cell the later layer, the one drawn above, wins,
 * and an unpainted cell lets the layer below show through. Hidden layers count, since hidden is view state.
 *
 * @param map The map.
 * @returns The folded zones.
 */
export function foldZones(map: MapDocument): FoldedZones {
  const cells = new Uint8Array(map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE);
  const types: string[] = [];
  const indexOf = new Map<string, number>();
  for (const layer of map.layers) {
    if (layer.kind !== "zone" || layer.types.length === 0) {
      continue;
    }
    const remap = layer.types.map((id) => {
      let index = indexOf.get(id);
      if (index === undefined) {
        types.push(id);
        index = types.length;
        indexOf.set(id, index);
      }
      return index;
    });
    const length = Math.min(cells.length, layer.cells.length);
    for (let i = 0; i < length; i += 1) {
      const value = layer.cells[i] as number;
      if (value !== 0) {
        cells[i] = remap[value - 1] as number;
      }
    }
  }
  return { cells, types: compactInPlace(types, cells) };
}

/**
 * The 1-based table index of a type id, adding it to the table when absent.
 *
 * @param types The table, grown in place.
 * @param id The id, or null for unpainted.
 * @returns The index, 0 for null.
 */
function indexFor(types: string[], id: string | null): number {
  if (id === null) {
    return 0;
  }
  const found = types.indexOf(id);
  if (found >= 0) {
    return found + 1;
  }
  types.push(id);
  return types.length;
}

/**
 * Checks a value a zone cell may hold: a non-empty type id, or null.
 *
 * @param value The value.
 * @param what What holds or writes it, the start of the message.
 * @returns The value.
 * @throws Error when the value is neither.
 */
export function zoneValue(value: unknown, what: string): string | null {
  if (value === null || (typeof value === "string" && value !== "")) {
    return value;
  }
  throw new Error(`${what} a type id or none, not ${JSON.stringify(value)}`);
}

/**
 * Names a cell's value for a message.
 *
 * @param id The type id, or null.
 * @returns The id, or `none`.
 */
function named(id: string | null): string {
  return id ?? "none";
}

/**
 * Writes cell edits into a copy of a zone layer and returns it canonical. Each cell must hold its `before`
 * type. The table grows by any new type first, so a stroke that replaces the last cell of one type with another
 * keeps a byte free for it only once the result is compacted; the table is refused when it would pass
 * `MAX_ZONE_TYPES` even after compaction.
 *
 * @param layer The layer.
 * @param edits The cells, each by index with its type before and after.
 * @returns The layer, canonical.
 * @throws Error when an index lies outside the grid, a cell does not hold its `before` type, or the layer would
 *   hold more types than a byte indexes.
 */
export function editZoneCells(layer: ZoneLayer, edits: readonly ZoneCellChange[]): ZoneLayer {
  const length = layer.cells.length;
  for (const edit of edits) {
    if (!Number.isInteger(edit.index) || edit.index < 0 || edit.index >= length) {
      throw new Error(`cell index ${edit.index} is outside the grid of ${length}`);
    }
    zoneValue(edit.before, "a zone cell holds");
    zoneValue(edit.after, "a zone cell holds");
    const held = zoneTypeAt(layer, edit.index);
    if (held !== edit.before) {
      throw new Error(`cell ${edit.index} holds ${named(held)}, not the recorded before value ${named(edit.before)}`);
    }
  }
  const types = [...layer.types];
  const wide = new Uint16Array(layer.cells);
  for (const edit of edits) {
    wide[edit.index] = indexFor(types, edit.after);
  }
  return canonicalFromWide(layer, types, wide);
}

/**
 * Fills listed cells of a zone layer holding one type with another, returning it canonical.
 *
 * @param layer The layer.
 * @param indices The cells.
 * @param before The type each holds, or null.
 * @param after The type each gets, or null.
 * @returns The layer, canonical.
 * @throws Error when a value is not a type id or null, an index lies outside the grid, a cell does not hold
 *   `before`, or the layer would hold more types than a byte indexes.
 */
export function fillZoneCells(layer: ZoneLayer, indices: Readonly<Uint32Array>, before: unknown, after: unknown): ZoneLayer {
  const from = zoneValue(before, "a zone fill writes");
  const to = zoneValue(after, "a zone fill writes");
  const length = layer.cells.length;
  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i] as number;
    if (index >= length) {
      throw new Error(`fill index ${index} is outside the grid of ${length}`);
    }
    const held = zoneTypeAt(layer, index);
    if (held !== from) {
      throw new Error(`cell ${index} holds ${named(held)}, not the recorded before value ${named(from)}`);
    }
  }
  const types = [...layer.types];
  const value = indexFor(types, to);
  const wide = new Uint16Array(layer.cells);
  for (let i = 0; i < indices.length; i += 1) {
    wide[indices[i] as number] = value;
  }
  return canonicalFromWide(layer, types, wide);
}

/**
 * Compacts a grid that may index past a byte into a canonical layer.
 *
 * @param layer The layer the grid belongs to.
 * @param types The table the grid indexes.
 * @param wide The grid, 16 bits a cell.
 * @returns The layer, canonical.
 * @throws Error when the ids the grid uses do not fit a byte.
 */
function canonicalFromWide(layer: ZoneLayer, types: readonly string[], wide: Uint16Array): ZoneLayer {
  const remap = new Uint16Array(types.length + 1);
  const compact: string[] = [];
  const cells = new Uint8Array(wide.length);
  for (let i = 0; i < wide.length; i += 1) {
    const value = wide[i] as number;
    if (value === 0) {
      continue;
    }
    if (remap[value] === 0) {
      compact.push(types[value - 1] as string);
      if (compact.length > MAX_ZONE_TYPES) {
        throw new Error(`layer ${layer.id} would hold ${compact.length} zone types; a layer holds at most ${MAX_ZONE_TYPES}`);
      }
      remap[value] = compact.length;
    }
    cells[i] = remap[value] as number;
  }
  return { ...layer, types: compact, cells };
}
